import { useQuery } from "@tanstack/react-query";
import { FinancialStatus } from "@hybrid-escrow/shared";
import { apiClient, type ListingsQuery } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { POLL } from "../lib/polling";

export function useOrders(query: ListingsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.orders(query),
    queryFn: ({ signal }) => apiClient.getOrders(query, signal),
    placeholderData: (previous) => previous,
    refetchInterval: POLL.list,
  });
}

/** Financial states where chain confirmation is still in flight and worth polling for. */
const IN_FLIGHT_STATUSES: readonly string[] = [
  FinancialStatus.PENDING_FUNDING,
  FinancialStatus.FUNDED,
  FinancialStatus.DISPUTED,
];

export function useOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.order(orderId ?? ""),
    queryFn: ({ signal }) => apiClient.getOrder(orderId as string, signal),
    enabled: Boolean(orderId),
    // Keep showing the order while a refetch (e.g. right after releasing funds) is in flight,
    // instead of dropping to a "Loading order…" screen. Scoped to the same order id so one
    // order's data never stands in for another's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === orderId ? previous : undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.financialStatus;
      // PENDING_FUNDING waits on the chain; FUNDED/DISPUTED wait on the other party (release,
      // dispute resolution). Settled orders (RELEASED/REFUNDED/EXPIRED/FAILED) stop polling.
      if (!status) return false;
      if (status === FinancialStatus.PENDING_FUNDING) return 5_000;
      return IN_FLIGHT_STATUSES.includes(status) ? POLL.live : false;
    },
  });
}
